/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { formatMoney } from '../../../shared/format/public-api';
import type { PurchasesBySupplierRow } from '../../domain/reportsPurchasesTypes';
import type { SupplierPerformanceRow } from '../../domain/reportsSupplierPerformanceTypes';

type UnifiedSupplierRow = {
  key: string;
  name: string;
  purchased: number;
  receptions: number;
  perf: SupplierPerformanceRow | undefined;
};

function buildUnifiedSupplierRows(
  bySupplier: PurchasesBySupplierRow[],
  performance: SupplierPerformanceRow[],
): UnifiedSupplierRow[] {
  const perfBySupplier = new Map(performance.map((p) => [p.supplierId ?? p.supplierName, p]));
  const seen = new Set<string>();
  const rows = bySupplier.map((s) => {
    const key = s.supplierId ?? s.supplierName;
    seen.add(key);
    return { key, name: s.supplierName, purchased: s.total, receptions: s.receptionCount, perf: perfBySupplier.get(key) };
  });
  for (const p of performance) {
    const key = p.supplierId ?? p.supplierName;
    if (seen.has(key)) continue;
    rows.push({ key, name: p.supplierName, purchased: p.totalPurchased, receptions: 0, perf: p });
  }
  return rows;
}

export function ReportsSuppliersUnifiedSection({
  bySupplier,
  performance,
  onSupplierPress,
}: {
  bySupplier: PurchasesBySupplierRow[];
  performance: SupplierPerformanceRow[];
  onSupplierPress?: (row: PurchasesBySupplierRow) => void;
}) {
  const rows = buildUnifiedSupplierRows(bySupplier, performance);

  return (
    <>
      <ReportsBlockHeading label="Proveedores" />
      {rows.length === 0 ? (
        <ModuleEmptyState variant="nest" title="Sin datos" subtitle="No hay compras ni recepciones en el período." />
      ) : (
        <PaperSheet padded testID="reports-compras-proveedores">
          {rows.slice(0, 10).map((row, i) => {
            const supplierRow = bySupplier.find(
              (s) => (s.supplierId ?? s.supplierName) === row.key,
            );
            const inner = (
              <>
              <View style={styles.reportsDenseListTitleBlock}>
                <Text style={styles.reportsDenseListTitle}>{row.name}</Text>
                <Text style={styles.reportsDenseListMeta}>
                  {row.receptions > 0 ? `${row.receptions} recepciones` : 'Sin recepciones'}
                  {row.perf ? ` · ${row.perf.totalOrders} OCs · ${row.perf.receivedOrders} recibidas` : ''}
                </Text>
                {row.perf ? (
                  <Text style={styles.reportsDenseListMeta}>
                    Fulfillment {row.perf.fulfillmentRate}%
                    {row.perf.partialOrders > 0 ? ` · ${row.perf.partialOrders} parciales` : ''}
                    {row.perf.cancelledOrders > 0 ? ` · ${row.perf.cancelledOrders} canceladas` : ''}
                    {row.perf.avgDeliveryDays != null ? ` · entrega ${row.perf.avgDeliveryDays}d` : ''}
                  </Text>
                ) : null}
              </View>
              <Text style={styles.reportsDenseListMoney}>{formatMoney(row.purchased)}</Text>
              </>
            );
            if (onSupplierPress && supplierRow?.supplierId) {
              return (
                <Pressable
                  key={row.key}
                  accessibilityRole="button"
                  accessibilityLabel={`Ver cuenta corriente de ${row.name} en Compras`}
                  testID={`reports-compras.supplier.${row.key}`}
                  onPress={() => onSupplierPress(supplierRow)}
                  style={[styles.reportsDenseListMetricsRow, i > 0 && styles.reportsDenseListRowBorder]}
                >
                  {inner}
                </Pressable>
              );
            }
            return (
              <View key={row.key} style={[styles.reportsDenseListMetricsRow, i > 0 && styles.reportsDenseListRowBorder]}>
                {inner}
              </View>
            );
          })}
        </PaperSheet>
      )}
    </>
  );
}
